import { PdfPageViewer } from '@tempots/beatui'
import {
  attr,
  computedOf,
  Ensure,
  html,
  prop,
  type Prop,
  Repeat,
  Signal,
} from '@tempots/dom'
import { ScaledPage } from './scaled-page'
import { computePreviewCrop } from './preview-crop'
import {
  renderDesignToHtml,
  type PageVariant,
} from '@rh/shared/templates/design-renderer'
import type { SavedDesign } from '@rh/shared/schemas/design'
import type { TokenResolutionData } from '@rh/shared/templates/design-tokens'
import {
  computeUniformTrim,
  type ContentBoundsReport,
  type PageTrim,
} from '@rh/shared/utils/content-trim'
import { simpleMarkdown } from '@rh/shared/index'

const MAX_PREVIEW_PAGES = 20

const NO_IMAGES: Record<string, string> = {}

function pageVariantFor(design: SavedDesign, pageNumber: number): PageVariant {
  if (pageNumber <= 1) return 'first'
  return design.pageMode === 'firstAndSubsequent' ||
    design.pageMode === 'firstOnly'
    ? 'subsequent'
    : 'first'
}

type RenderedDesign = ReturnType<typeof renderDesignToHtml>

/**
 * One preview page as two stacked layers, mirroring exactly how
 * composeProcessedResume composites the final PDF: the actual resume page
 * (via pdf.js) sits *underneath*, and the design's chrome HTML — the same
 * renderDesignToHtml output the server uses — is layered *on top*, with
 * everything outside its explicitly-styled components left transparent so
 * the resume shows through.
 */
function DesignPreviewPage({
  rendered,
  resume,
  page,
  pdfPageSize,
  trim,
  onPageCountKnown,
}: {
  rendered: Signal<RenderedDesign>
  resume: Signal<ArrayBuffer>
  page: number
  /**
   * Natural PDF page size, shared across all page components: pdf.js only
   * fires onLoadComplete for the page that triggers the document load, so
   * later pages must reuse the size learned from the first page rather
   * than each waiting for a callback that may never come.
   */
  pdfPageSize: Prop<{ w: number; h: number } | null>
  /**
   * Uniform top/bottom crop of the source page (PDF points), mirroring the
   * server composer's margin trim. Null ⇒ render untrimmed, like the
   * composer without content bounds.
   */
  trim: Signal<PageTrim | null>
  onPageCountKnown?: (count: number) => void
}) {
  const chromeHtml = rendered.map(r => r.html)
  const placementRect = rendered.map(r => r.placementRect)
  // Scale the PDF page to the placement rect explicitly (fit:'none' +
  // computed scale) instead of relying on fit:'contain', which measures its
  // container only at mount and goes stale/racy when the slot resizes as
  // the band grows or shrinks with form edits. The composer centers the
  // resume inside the rect, so the slot is a centering flexbox to match.
  // With a trim, the fit is computed against the CROPPED page height and the
  // full-page render is clipped inside a crop box — the DOM equivalent of
  // the composer's embedPage bounding box (see computePreviewCrop).
  const crop = computedOf(
    placementRect,
    pdfPageSize,
    trim
  )((r, s, t) =>
    s
      ? computePreviewCrop(r, s, t)
      : { scale: 1, cropWidth: 0, cropHeight: 0, offsetY: 0 }
  )
  const pdfScale = crop.map(c => c.scale)

  return html.div(
    attr.style(
      'position:absolute; inset:0; background:#ffffff; box-shadow:0 1px 6px rgba(0,0,0,0.15);'
    ),
    html.div(
      attr.style(
        placementRect.map(
          r =>
            `position:absolute; left:${r.x}px; top:${r.y}px; width:${r.width}px; height:${r.height}px; overflow:hidden; display:flex; align-items:center; justify-content:center;`
        )
      ),
      html.div(
        attr.style(
          crop.map(c =>
            c.cropWidth > 0
              ? `width:${c.cropWidth}px; height:${c.cropHeight}px; overflow:hidden;`
              : ''
          )
        ),
        html.div(
          attr.style(
            crop.map(c => (c.offsetY !== 0 ? `margin-top:${c.offsetY}px;` : ''))
          ),
          PdfPageViewer({
            source: resume,
            page,
            fit: 'none',
            scale: pdfScale,
            renderAnnotationLayer: false,
            renderTextLayer: false,
            onLoadComplete: async ({ pdfDoc }) => {
              const pdfPage = await pdfDoc.getPage(page)
              const [, , w, h] = pdfPage.view
              pdfPageSize.set({ w, h })
              onPageCountKnown?.(pdfDoc.numPages)
            },
          })
        )
      )
    ),
    html.div(
      attr.style('position:absolute; inset:0; pointer-events:none;'),
      attr.innerHTML(chromeHtml)
    )
  )
}

/**
 * Cover page for the candidate write-up: markdown rendered inside the
 * design's chrome-trimmed writeup rect (with the same 24px padding the
 * server's renderMarkdownToPdf applies), under the design chrome — matching
 * how composeProcessedResume builds the real cover page from the
 * first-page template.
 */
function WriteupPage({
  rendered,
  writeup,
}: {
  rendered: Signal<RenderedDesign>
  writeup: Signal<string>
}) {
  const chromeHtml = rendered.map(r => r.html)
  // The chrome-trimmed writeup rect, not the raw placement rect: write-up
  // text renders on top of the chrome, so it must clear a header band that
  // covers the placeholder's top (same rule the server composer applies).
  const placementRect = rendered.map(r => r.writeupRect)

  return html.div(
    attr.style(
      'position:absolute; inset:0; background:#ffffff; box-shadow:0 1px 6px rgba(0,0,0,0.15);'
    ),
    html.div(
      attr.style(
        placementRect.map(
          r =>
            `position:absolute; left:${r.x}px; top:${r.y}px; width:${r.width}px; height:${r.height}px; overflow:hidden;`
        )
      ),
      html.div(
        attr.style('width:100%; height:100%; overflow:hidden;'),
        attr.innerHTML(
          writeup.map(
            content =>
              `<div style="padding:24px;color:#0f172a;font-family:Arial, sans-serif;font-size:16px;line-height:1.4;">${simpleMarkdown(content)}</div>`
          )
        )
      )
    ),
    html.div(
      attr.style('position:absolute; inset:0; pointer-events:none;'),
      attr.innerHTML(chromeHtml)
    )
  )
}

export interface TrialDesignPreviewPagesOptions {
  design: Signal<SavedDesign>
  tokenData: Signal<TokenResolutionData>
  /**
   * Image data URLs keyed by the design's assetKeys (an admin-uploaded
   * default design may carry images; the checked-in default never does).
   * Absent ⇒ image components render empty.
   */
  images?: Signal<Record<string, string>>
  writeup: Signal<string | null>
  resume: Signal<ArrayBuffer>
  /**
   * Server-measured content margins for the uploaded document (null while
   * unavailable). Drives the same margin trim the composer applies, so the
   * preview matches the downloaded PDF.
   */
  contentBounds?: Signal<ContentBoundsReport | null>
}

/**
 * Live preview for the public trial page, driven by the same SavedDesign →
 * renderDesignToHtml pipeline the server (and the logged-in Customize
 * editor) uses — so what a trial visitor sees is what the downloaded PDF
 * actually looks like.
 */
export function TrialDesignPreviewPages({
  design,
  tokenData,
  images,
  writeup,
  resume,
  contentBounds,
}: TrialDesignPreviewPagesOptions) {
  const pageCount = prop(1)
  const handlePageCount = (count: number) => {
    pageCount.set(Math.min(count, MAX_PREVIEW_PAGES))
  }
  // Learned once from whichever viewer triggers the document load; shared
  // so every page scales identically (see DesignPreviewPage.pdfPageSize).
  const pdfPageSize = prop<{ w: number; h: number } | null>(null)

  // Same trim decision the server composer makes (shared computeUniformTrim):
  // uniform across pages, vetoed to null on any doubt. All preview pages
  // share one page size, matching the composer's per-page MediaBox reads for
  // the uniform-page-size documents this preview can represent.
  const trim = computedOf(
    contentBounds ?? prop<ContentBoundsReport | null>(null),
    pageCount,
    pdfPageSize
  )((bounds, count, size) =>
    bounds && size
      ? computeUniformTrim(
          bounds,
          count,
          Array.from({ length: count }, () => ({
            width: size.w,
            height: size.h,
          }))
        )
      : null
  )

  const renderVariant = (page: number) =>
    computedOf(
      design,
      tokenData,
      images ?? prop(NO_IMAGES)
    )((d, td, imgs) =>
      renderDesignToHtml(d, pageVariantFor(d, page), {
        tokenData: td,
        resolveImageUrl: key => imgs[key],
      })
    )

  const renderedFirst = renderVariant(1)
  const pageSize = renderedFirst.map(r => ({
    w: r.placementRect.pageWidth,
    h: r.placementRect.pageHeight,
  }))

  return html.div(
    attr.class('h-full overflow-auto p-4 space-y-4'),
    Ensure(writeup, writeupValue =>
      ScaledPage(
        pageSize,
        WriteupPage({ rendered: renderedFirst, writeup: writeupValue })
      )
    ),
    ScaledPage(
      pageSize,
      DesignPreviewPage({
        rendered: renderedFirst,
        resume,
        page: 1,
        pdfPageSize,
        trim,
        onPageCountKnown: handlePageCount,
      })
    ),
    Repeat(
      pageCount.map(c => Math.max(c - 1, 0)),
      // counter is 1-based, so the first repeated element is page 2
      ({ counter }) =>
        ScaledPage(
          pageSize,
          DesignPreviewPage({
            rendered: renderVariant(counter + 1),
            resume,
            page: counter + 1,
            pdfPageSize,
            trim,
          })
        )
    )
  )
}
